import { Button, Box, Typography, Link } from '@mui/material';
import { FC, useState, Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { JobseekerCreateDto } from '@libs/dto';
import { getErrors } from '@carcareers/utils';
import { useForm, useSnackbar } from '@carcareers/contexts';
import {
  useAuthCreateJobseekerMutation,
  useAuthCreateExternalOtpMutation,
  useAuthVerifyExternalOtpMutation,
  useResumeCreateMutation,
} from '@carcareers/store';

interface CreateJobseekerStepNavProps {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

enum Step {
  AccountInformation = 0,
  VerifyEmail = 1,
  UploadResume = 2,
  Experience = 3,
  Education = 4,
}

export const CreateJobseekerStepNav: FC<CreateJobseekerStepNavProps> = ({ activeStep, setActiveStep }) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const { data, errors, setData, setErrors } = useForm<JobseekerCreateDto>();

  const [createExternalOtp] = useAuthCreateExternalOtpMutation();
  const [verifyExternalOtp] = useAuthVerifyExternalOtpMutation();
  const [createJobseeker] = useAuthCreateJobseekerMutation();
  const [resumeCreate] = useResumeCreateMutation();

  const errorProperties: Record<Step, string[]> = {
    [Step.AccountInformation]: [
      'user.firstName',
      'user.lastName',
      'user.email',
      'user.phone',
      'password',
      'confirmPassword',
    ],
    [Step.VerifyEmail]: ['otp'],
    [Step.UploadResume]: ['resume'],
    [Step.Experience]: ['experiences'],
    [Step.Education]: ['educations'],
  };

  const clearStepErrors = (step: Step) => {
    const errorsToRemove = errorProperties[step];
    setErrors(errors.filter((error) => !errorsToRemove.some((property) => error.property.includes(property))));
  };

  const handleAccountInformation = async () => {
    if (data?.user?.isEmailVerified) {
      clearStepErrors(Step.AccountInformation);
      setActiveStep(Step.UploadResume);
      setLoading(false);
      return;
    }

    const requestData = {
      email: data?.user?.email?.toLowerCase(),
    };

    const response = await createExternalOtp(requestData);

    if (!response.error) {
      clearStepErrors(Step.AccountInformation);
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      const errors = getErrors(response);
      setErrors(errors);
    }

    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!data?.otp) {
      setErrors([{ property: 'otp', errors: ['OTP is required.'] }]);
      setLoading(false);
      return;
    }

    if (data?.user?.isEmailVerified) {
      clearStepErrors(Step.VerifyEmail);
      setActiveStep((prevStep) => prevStep + 1);
      setLoading(false);
      return;
    }

    const requestData = {
      email: data?.user?.email.toLowerCase(),
      otp: data?.otp,
    };

    const { error } = await verifyExternalOtp(requestData);

    if (!error) {
      setData('user.isEmailVerified', true);
      clearStepErrors(Step.VerifyEmail);
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      setErrors([{ property: 'otp', errors: ['Invalid OTP. Please try again.'] }]);
    }

    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!data?.resume && !data?.experiences?.length) {
      setErrors([{ property: 'resume', errors: ['Please add your resume or fill out your job experience.'] }]);
      setActiveStep(Step.UploadResume);
      setLoading(false);
      return;
    }

    const transformedData = {
      ...data,
      user: {
        ...data.user,
        email: data.user.email.toLowerCase(),
      },
    };
    const response = await createJobseeker(transformedData);

    if (!response.error) {
      const resumeFile = data?.resume as File | undefined;

      if (response.data && resumeFile) {
        const formData = new FormData();
        formData.append('jobseekerId', response.data.id.toString());
        formData.append('resumes', resumeFile);
        formData.append('isNewJobseeker', 'true');
        await resumeCreate(formData);
      }

      navigate('/auth/login', { state: { isJobseeker: true, isSignup: true } });
    } else {
      const errors = getErrors(response);
      setErrors(errors);

      // Determine which step contains the errors and navigate accordingly
      let targetStep = Step.AccountInformation; // Default to first step
      let errorMessage = 'Failed to create an account. Please try again.';

      if (
        errorProperties[Step.AccountInformation].some((property) => errors.some((error) => error.property === property))
      ) {
        targetStep = Step.AccountInformation;
        errorMessage = 'Please check your account information and try again.';
      } else if (
        errorProperties[Step.UploadResume].some((property) => errors.some((error) => error.property === property))
      ) {
        targetStep = Step.UploadResume;
        errorMessage = 'Please check your resume and try again.';
      } else if (
        errorProperties[Step.Experience].some((property) => errors.some((error) => error.property.includes(property)))
      ) {
        targetStep = Step.Experience;
        errorMessage = 'Please check your experience information and try again.';
      } else if (
        errorProperties[Step.Education].some((property) => errors.some((error) => error.property.includes(property)))
      ) {
        targetStep = Step.Education;
        errorMessage = 'Please check your education information and try again.';
      }

      setActiveStep(targetStep);
      toggleSnackbar('error', errorMessage);
    }

    setLoading(false);
  };

  const handleNext = async () => {
    setLoading(true);

    if (activeStep === Step.AccountInformation) {
      handleAccountInformation();
    } else if (activeStep === Step.VerifyEmail) {
      handleVerifyOtp();
    } else if (activeStep === Step.Education) {
      handleSubmit();
    } else {
      clearStepErrors(activeStep);
      setActiveStep((prevStep) => prevStep + 1);
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep === Step.AccountInformation) {
      navigate(-1);
    } else if (activeStep === Step.UploadResume && data?.user?.isEmailVerified) {
      // Skip back to account information step if email is verified.
      setActiveStep(Step.AccountInformation);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  return (
    <Box sx={{ mt: 12 }}>
      {activeStep === Step.UploadResume && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Don't have a resume handy?
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={handleNext}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            Fill out experience instead
          </Link>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button size="large" variant="outlined" color="secondary" onClick={handleBack}>
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        <Button size="large" variant="contained" color="primary" onClick={handleNext} loading={loading}>
          {activeStep === Step.Education ? 'Create Account' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateJobseekerStepNav;
